import React, {
  useCallback, useEffect, useState,
} from 'react';
import { observer } from 'mobx-react';
import { Droppable } from 'react-beautiful-dnd';
import { Virtuoso } from 'react-virtuoso';
import styled from 'styled-components';
import { Theme } from '@routific/hive';
import { useStore } from '../../../../../store/mst';
import useVirtuoso from '../../../../../hooks/useVirtuoso';
import { RouteDtoModelType, RouteTimelineDtoModelType } from '../../../../../store/mst/models';
import TimelineCard from './TimelineCard';
import VisitTimelineCard from './TimelineCard/VisitTimelineCard';
import { DragDropProps } from './TimelineCard/TimelineDragListItem';
import {
  applyDeliveryStopSelection,
} from '../../../RoutesGantt/RoutesGanttChart/stopSelection';

const CardPadding = styled.div`
  padding: ${Theme.Spacing.xsSpacing} ${Theme.Spacing.mdSpacing};
`;

interface Props {
  route: RouteDtoModelType;
  headerElementRef?: React.RefObject<HTMLDivElement | null>;
  stateless?: boolean;
  isBreakdownLoading?: boolean;
  dragDropProps?: DragDropProps;
}

const Card: React.FC<any> = observer(({
  provided, item, isDragging, color, isBreakdownLoading,
}) => (
  <div
    {...provided.draggableProps}
    ref={provided.innerRef}
    style={{
      ...provided.draggableProps.style,
      transform: !isDragging
        ? 'none'
        : provided.draggableProps.style.transform,
    }}
  >
    <VisitTimelineCard
      event={item}
      routeColor={color}
      stateless={false}
      hideDraggableIcon={false}
      dragHandleProps={provided.dragHandleProps}
      isDraggable
      isClone
      isBreakdownLoading={isBreakdownLoading}
    />
  </div>
));

const HeightPreservingItem = ({ children, color, ...props }: any) => {
  const [size, setSize] = useState(0);
  const knownSize = props['data-known-size'];
  useEffect(() => {
    setSize((prevSize: number) => (knownSize === 0 ? prevSize : knownSize));
  }, [knownSize]);
  return (
    <div
      {...props}
      className="height-preserving-container"
      // check styling in the style tag below
      style={{ '--child-height': `${size}px`, position: 'relative' }}
    >
      {children}
    </div>
  );
};

const RouteTimeline: React.FC<Props> = ({
  route,
  dragDropProps,
  stateless = false,
  isBreakdownLoading = false,
}) => {
  const store = useStore();

  const { virtuosoRef, initialIndex, setVisibleRange } = useVirtuoso({
    // Get the actual stop index in the timeline — sequence apparently also includes individual orders??
    scrollToIndex: store.ui.selectedRouteEvent?.route?.timeline?.findIndex((s: RouteTimelineDtoModelType) => s.eventUuid === store.ui.selectedRouteEvent?.eventUuid),
  });
  const selectedItemCount = (store.mapView.selectedItems?.stops?.length ?? 0)
    + (store.mapView.selectedItems?.unscheduledOrders?.length ?? 0);
  const hasMultiSelection = selectedItemCount > 1;

  const handleDeliveryStopSelect = useCallback((event: RouteTimelineDtoModelType, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (!event.isStopEvent || !event.id) {
      return;
    }

    const selectedSingleStop = store.ui.selectedRouteEvent?.isStopEvent
      ? store.ui.selectedRouteEvent
      : store.ui.selectedVisit?.timeline;
    applyDeliveryStopSelection({
      route,
      stopId: event.id,
      anchorsByRoute: store.mapView.selectedStopAnchorsByRoute,
      selectedStops: store.mapView.selectedItems?.stops ?? [],
      selectedUnscheduledOrders: store.mapView.selectedItems?.unscheduledOrders ?? [],
      selectedSingleStop,
      event: e,
      setSelectedItems: (itemsToSelect) => store.mapView.setSelectedItems(itemsToSelect),
      setSelectedStopAnchorsByRoute: (anchorsByRoute) => store.mapView.setSelectedStopAnchorsByRoute(anchorsByRoute),
      selectSingleStop: () => store.ui.selectOrderAndStopByUuid(undefined, event.eventUuid),
      clearSingleStop: () => store.ui.selectRouteEvent(undefined),
    });
  }, [route, store.mapView, store.ui]);

  if (route?.totalStopsCount === 0) {
    return (
      <TimelineCard route={route} index={-1} />
    );
  }

  return (
    <Droppable
      droppableId="route-timeline"
      mode="virtual"
      renderClone={(provided, snapshot, rubric) => (
        <Card provided={provided} isDragging={snapshot.isDragging} item={route.timeline[rubric.source.index]} color={route.color} isBreakdownLoading={isBreakdownLoading} />
      )}
    >
      {(provided, snapshot) => (
        <Virtuoso
          components={{
            Item: HeightPreservingItem,
          }}
          ref={virtuosoRef}
          rangeChanged={setVisibleRange}
          initialTopMostItemIndex={initialIndex}
          overscan={40}
          scrollerRef={provided.innerRef as (element: HTMLElement | Window | null) => void}
          data={route.timeline}
          style={{ width: '100%', height: '100%' }}
          itemContent={(index: number, item: RouteTimelineDtoModelType) => (
            <CardPadding>
              <TimelineCard
                route={route}
                event={item}
                index={index}
                dragDropProps={dragDropProps}
                stateless={stateless}
                isOtherDragging={snapshot.isDraggingOver}
                isBreakdownLoading={isBreakdownLoading}
                disableDrag={hasMultiSelection}
                onDeliveryStopSelect={handleDeliveryStopSelect}
              />
            </CardPadding>
          )}
        />
      )}
    </Droppable>
  );
};

export default observer(RouteTimeline);
